/**
 * The member's own outreach policy as the form edits it, and the
 * `updateNotificationSettings` input that saves it.
 *
 * The channel toggles read as "on", the backend stores the opt-out, so each is
 * inverted on the way in and out.
 */

/** Whether the assistant may reach out unprompted on each channel. */
export interface IOutreachChannels {
  text: boolean;
  email: boolean;
}

export interface IOutreachDraft {
  activeHours: { start: string; end: string };
  channels: IOutreachChannels;
}

/** The saved policy, as `notificationSettings` returns it. */
export interface ISavedOutreachPolicy {
  activeHours: { start: string; end: string };
  outreachTextOptedOut: boolean;
  outreachEmailOptedOut: boolean;
}

export interface IOutreachSettingsInput {
  outreachActiveHours?: { start: string; end: string };
  outreachTextOptedOut?: boolean;
  outreachEmailOptedOut?: boolean;
}

/** The member's contact points and when each was proven, as `getUser` returns them. */
export interface IOutreachContact {
  phoneNumber?: string | null;
  phoneNumberVerifiedAt?: string | null;
  email: string;
  emailVerifiedAt?: string | null;
}

/** Why each channel cannot carry outreach whatever its toggle says, or `null` where it can. */
export interface IOutreachChannelBlocks {
  text: 'unverified' | 'stopped' | null;
  email: 'unverified' | null;
}

/**
 * The gates the backend's channel verdict applies besides the member's own
 * opt-out, in its order: an unproven number before a carrier STOP.
 */
export const outreachChannelBlocks = (
  contact: IOutreachContact,
  textConsentRevoked: boolean
): IOutreachChannelBlocks => {
  let text: IOutreachChannelBlocks['text'] = null;
  if (!contact.phoneNumber || !contact.phoneNumberVerifiedAt) {
    text = 'unverified';
  } else if (textConsentRevoked) {
    text = 'stopped';
  }
  return { text, email: contact.emailVerifiedAt ? null : 'unverified' };
};

export const outreachChannelsFromPolicy = (
  policy: Pick<ISavedOutreachPolicy, 'outreachTextOptedOut' | 'outreachEmailOptedOut'>
): IOutreachChannels => ({ text: !policy.outreachTextOptedOut, email: !policy.outreachEmailOptedOut });

/** Only the fields that differ from what is saved, or `null` when nothing does. */
export const buildOutreachSettingsInput = (
  draft: IOutreachDraft,
  saved: ISavedOutreachPolicy
): IOutreachSettingsInput | null => {
  const input: IOutreachSettingsInput = {};
  // Both edges move together — the stored window is one key, replaced whole.
  if (draft.activeHours.start !== saved.activeHours.start || draft.activeHours.end !== saved.activeHours.end) {
    input.outreachActiveHours = draft.activeHours;
  }
  if (draft.channels.text === saved.outreachTextOptedOut) {
    input.outreachTextOptedOut = !draft.channels.text;
  }
  if (draft.channels.email === saved.outreachEmailOptedOut) {
    input.outreachEmailOptedOut = !draft.channels.email;
  }
  return Object.keys(input).length === 0 ? null : input;
};
